/**
 * Кто смотрит — по ответу сервера, а не по локальному переключателю.
 *
 * В прототипе персона выбиралась карточкой и лежала в localStorage. Здесь тот
 * же контракт (`usePersona()`) питается каноническим `/auth/me`: роль, область
 * и признак «можно ли судить» приходят с сервера, и второй копии прав на
 * клиенте не заводится. Переключатель персон заменён входом (решение 7 плана
 * `astra-on-canon`); экраны Astra при этом не переписаны — они как и раньше
 * спрашивают `usePersona()`.
 *
 * Хозяйство фермера — тоже из `/auth/me` (`principal.actors`): выбрать чужое
 * хозяйство нельзя, а из нескольких своих выбирается только своё (см.
 * `personas.ts`).
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/context';
import { ShellSkeleton } from '../shell/ShellSkeleton';
import { useAnalysisContext } from '../../analysis/context';
import {
  dropOwnFarm,
  homeFor,
  isRouteAllowed,
  ownFarms,
  pickOwnFarm,
  roleFromCanon,
  writeOwnFarm,
  type Persona,
} from './personas';

interface RoleContextValue {
  /**
   * Кто смотрит. Не вошедший — наблюдатель с признаком `anonymous`, а не
   * `null`: чтение района открыто без входа, и «никого нет» было бы неправдой.
   * `null` остаётся только значением по умолчанию контекста вне провайдера.
   */
  persona: Persona | null;
  /**
   * Выбор одного из СВОИХ хозяйств для аккаунта роли `farm`, связанного с
   * несколькими. Чужое хозяйство не принимается: область отдаёт сервер.
   */
  setPersona: (next: Persona) => void;
  /** Выход: серверная сессия закрывается, локальная привязка снимается. */
  signOut: () => void;
  /** Первая загрузка `/auth/me` ещё идёт: «не вошёл» утверждать рано. */
  loading: boolean;
}

const RoleContext = createContext<RoleContextValue>({
  persona: null,
  setPersona: () => undefined,
  signOut: () => undefined,
  loading: true,
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const { principal, loading, signOut: canonSignOut, reload } = useAuth();
  /** Выбор среди своих хозяйств: пересчитать персону сразу, не ждя `/auth/me`. */
  const [choice, setChoice] = useState<string | null>(null);

  const persona = useMemo<Persona | null>(() => {
    // Без входа — наблюдатель, а не экран входа.
    //
    // Чтение района сервер отдаёт анониму (`backend/serving/scope.py`:
    // область `district`, «пока чтение открыто»). Уводить такого человека на
    // `/login` значило бы закрыть на клиенте то, что сервер открывает, и
    // развести клиент с сервером в вопросе «кому открыто чтение». Прежняя
    // оболочка каноники рисовала анониму те же экраны и держала вход в шапке
    // — здесь то же самое, но роль названа явно. Сторона развилки выбрана
    // под допущением (см. `decisions_needed` среза) и держится тестом
    // «аноним остаётся на районе».
    if (!principal || !principal.is_authenticated) {
      return { role: 'observer', anonymous: true };
    }
    const role = roleFromCanon(principal.roles);
    // Наблюдатель сегодня видит те же экраны и те же действия, что хоким:
    // требование спеки «интерфейс без действий» ещё не выполнено и честно
    // не изображается признаком, который никто не проверяет (см. `personas.ts`).
    if (role !== 'farmer') return { role };
    const own = pickOwnFarm(ownFarms(principal.actors));
    // Фермер без хозяйства — половина персоны: показывать нечего. Вход скажет,
    // что аккаунт не связан ни с одним хозяйством, либо попросит выбрать одно
    // из своих, если их несколько.
    if (!own) return { role };
    return { role, farmId: own.farmId, farmName: own.farmName };
  }, [principal, choice]);

  const setPersona = useCallback(
    (next: Persona) => {
      if (next.role !== 'farmer' || !next.farmId) return;
      const mine = ownFarms(principal?.actors ?? []);
      if (!mine.some((farm) => farm.farmId === next.farmId)) return;
      writeOwnFarm(next.farmId);
      setChoice(next.farmId);
      // Персона считается из принципала: перечитываем его, чтобы привязка
      // доехала до экранов тем же путём, что и всё остальное про вошедшего.
      reload();
    },
    [principal, reload],
  );

  const signOut = useCallback(() => {
    dropOwnFarm();
    setChoice(null);
    void canonSignOut();
  }, [canonSignOut]);

  const value = useMemo(
    () => ({ persona, setPersona, signOut, loading }),
    [persona, setPersona, signOut, loading],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function usePersona(): RoleContextValue {
  return useContext(RoleContext);
}

/**
 * Единственная проверка доступа в оболочке.
 *
 * Аноним НЕ уводится на вход: он смотрит как наблюдатель, потому что чтение
 * района сервер отдаёт без сессии. Фермера с маршрута станции возвращает в
 * его кабинет; фермера без хозяйства — на вход, где сказано почему. Это витрина правила, а не защита: данные разрешает сервер, и
 * «закрыто» здесь означает «не показано».
 */
export function RequirePersona() {
  const { persona, loading } = usePersona();
  const { pathname } = useLocation();
  const { linkSearch } = useAnalysisContext();
  const search = linkSearch();

  // Пока `/auth/me` не ответил, роли ещё нет: уводить по догадке нельзя.
  if (loading) {
    return <ShellSkeleton />;
  }

  if (!persona) {
    return <Navigate to="/login" replace state={{ from: pathname }} />;
  }

  if (persona.role === 'farmer' && !persona.farmId) {
    return <Navigate to="/login" replace state={{ from: pathname }} />;
  }

  if (!isRouteAllowed(persona.role, pathname)) {
    // Срез переживает перенаправление по роли: `/contours?as_of=…` должен
    // приводить фермера на его дом В ТОМ ЖЕ состоянии мира, а не в сегодня.
    return (
      <Navigate to={{ pathname: homeFor(persona.role), search }} replace />
    );
  }

  return <Outlet />;
}
